import { useEffect, useState } from "react";
import { http, track, getUTM, sha256, sessionId, errText, scrollTo, inr } from "../../lib/api";
import { Button } from "../ui/button";

const SCORES = [
  { v: "", l: "Prefer not to say" },
  { v: "above_750", l: "Above 750" },
  { v: "650_750", l: "650 to 750" },
  { v: "below_650", l: "Below 650" },
  { v: "unknown", l: "I do not know" },
];
const WINDOWS = ["anytime", "morning", "afternoon", "evening"];

export function LeadForm({ settings, prefill = {}, variant = "full", onDone }) {
  const products = settings?.products || {};
  const loanProducts = Object.entries(products).filter(
    ([k]) => !k.startsWith("tax_") && k !== "partner_signup"
  );
  const [step, setStep] = useState(0);
  const [data, setData] = useState({
    product: prefill.product || "personal_loan",
    amount_requested: prefill.amount_requested || 500000,
    name: "", mobile: "", city: "",
    preferred_call_window: "anytime", credit_score_range: "",
    source: prefill.source || "website", source_tool: prefill.source_tool || "",
  });
  const [consent, setConsent] = useState(false);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const [result, setResult] = useState(null);
  const set = (k, v) => setData((d) => ({ ...d, [k]: v }));

  useEffect(() => {
    track("form_start", { variant });
    // eslint-disable-next-line
  }, []);
  useEffect(() => {
    if (prefill.product) set("product", prefill.product);
    if (prefill.amount_requested) set("amount_requested", prefill.amount_requested);
    if (prefill.source) set("source", prefill.source);
    if (prefill.source_tool) set("source_tool", prefill.source_tool);
    // eslint-disable-next-line
  }, [JSON.stringify(prefill)]);

  const mobileOk = /^\+91[6-9]\d{9}$/.test("+91" + data.mobile.replace(/^\+?91/, ""));
  const normMobile = "+91" + data.mobile.replace(/\D/g, "").replace(/^91/, "").slice(-10);

  const next = () => {
    if (step === 0 && (!data.product || data.amount_requested < 0)) return;
    if (step === 1 && (data.name.trim().length < 2 || !/^[6-9]\d{9}$/.test(data.mobile.replace(/\D/g, "").slice(-10)))) {
      setErr("Enter your name and a valid 10-digit mobile starting 6-9.");
      return;
    }
    setErr("");
    track("step_complete", { step });
    setStep((s) => s + 1);
  };

  const submit = async () => {
    if (!consent) { setErr("Please agree to be contacted to continue."); return; }
    setBusy(true); setErr("");
    try {
      const text_hash = await sha256(settings.consent_text);
      track("consent_given", {});
      const payload = {
        ...data, mobile: normMobile,
        amount_requested: Number(data.amount_requested) || 0,
        consent: { version: settings.consent_version, text_hash, channels: ["call", "sms", "whatsapp"] },
        utm: getUTM(), session_id: sessionId(),
      };
      const { data: res } = await http.post("/leads", payload);
      track("lead_submitted", { lead_id: res.lead_id, status: res.status });
      setResult(res);
      onDone && onDone(res);
    } catch (e) {
      const d = e?.response?.data?.detail;
      setErr(d === "rate_limited" ? "Too many enquiries from your network. Please try later."
        : d === "consent_required" ? "Consent is required." : errText(d));
    } finally {
      setBusy(false);
    }
  };

  if (result) {
    return (
      <div data-testid="lead-success" className="text-center py-4">
        <div className="mx-auto w-12 h-12 rounded-full bg-[#e2f0ef] grid place-items-center mb-3">
          <span className="text-[#004040] text-2xl font-extrabold">✓</span>
        </div>
        <h3 className="text-xl font-extrabold">You're in. We'll call you.</h3>
        <p className="mt-2 text-sm text-[#6b6f76]">Your reference</p>
        <p data-testid="lead-id" className="text-2xl font-extrabold text-[#004040]">{result.lead_id}</p>
        <p className="mt-2 text-sm text-[#6b6f76]">Save this to track your application anytime.</p>
        {result.show_consulting_prompt && (
          <div className="mt-4 rounded-2xl bg-[#fff0e6] p-4 text-sm text-left">
            If a lender cannot approve you yet, our consultants can guide you.{" "}
            <button data-testid="success-plans-link" className="font-extrabold underline"
              onClick={() => { onDone && onDone(result); scrollTo("plans"); }}>
              See the guided plans →
            </button>
          </div>
        )}
        <button className="mt-4 text-sm font-bold underline" data-testid="lead-track-link"
          onClick={() => { onDone && onDone(result); scrollTo("track"); }}>
          Track this application
        </button>
      </div>
    );
  }

  const inputCls = "w-full min-h-[44px] rounded-2xl border-2 border-[#e1e2e5] px-4 py-2.5 font-medium focus:border-[#004040] outline-none bg-white";
  const labelCls = "block text-sm font-bold mb-1.5 mt-3";

  return (
    <div data-testid="lead-form">
      <div className="flex items-center gap-1.5 mb-3">
        {[0, 1, 2].map((i) => (
          <div key={i} className={`h-1.5 flex-1 rounded-full ${i <= step ? "bg-[#f06400]" : "bg-[#e1e2e5]"}`} />
        ))}
      </div>

      {step === 0 && (
        <div>
          <label className={labelCls} htmlFor="lf-product">What do you need?</label>
          <select id="lf-product" data-testid="lead-product" className={inputCls}
            value={data.product} onChange={(e) => set("product", e.target.value)}>
            {loanProducts.map(([k, l]) => <option key={k} value={k}>{l}</option>)}
          </select>
          <label className={labelCls} htmlFor="lf-amount">Amount needed: Rs {inr(data.amount_requested)}</label>
          <input id="lf-amount" data-testid="lead-amount" type="range" min="50000" max="5000000" step="10000"
            className="w-full accent-[#f06400]" value={data.amount_requested}
            onChange={(e) => set("amount_requested", Number(e.target.value))} />
          <Button data-testid="lead-next-0" onClick={next}
            className="w-full mt-4 bg-[#191c1f] hover:bg-black text-white rounded-full font-bold min-h-[44px]">
            Continue
          </Button>
        </div>
      )}

      {step === 1 && (
        <div>
          <label className={labelCls} htmlFor="lf-name">Your name</label>
          <input id="lf-name" data-testid="lead-name" className={inputCls} value={data.name}
            onChange={(e) => set("name", e.target.value)} placeholder="Full name" />
          <label className={labelCls} htmlFor="lf-mobile">Mobile</label>
          <div className="flex items-center rounded-2xl border-2 border-[#e1e2e5] focus-within:border-[#004040] overflow-hidden">
            <span className="px-3 text-[#6b6f76] font-bold">+91</span>
            <input id="lf-mobile" data-testid="lead-mobile" inputMode="numeric" maxLength={10}
              className="flex-1 min-h-[44px] px-2 py-2.5 outline-none" value={data.mobile}
              onChange={(e) => set("mobile", e.target.value.replace(/\D/g, "").slice(0, 10))}
              placeholder="10-digit number" />
          </div>
          <div className="grid grid-cols-2 gap-2">
            <div>
              <label className={labelCls} htmlFor="lf-city">City</label>
              <input id="lf-city" data-testid="lead-city" className={inputCls} value={data.city}
                onChange={(e) => set("city", e.target.value)} placeholder="City" />
            </div>
            <div>
              <label className={labelCls} htmlFor="lf-win">Best time to call</label>
              <select id="lf-win" data-testid="lead-window" className={inputCls}
                value={data.preferred_call_window} onChange={(e) => set("preferred_call_window", e.target.value)}>
                {WINDOWS.map((w) => <option key={w} value={w}>{w[0].toUpperCase() + w.slice(1)}</option>)}
              </select>
            </div>
          </div>
          <label className={labelCls} htmlFor="lf-score">Do you know your credit score range?</label>
          <select id="lf-score" data-testid="lead-score" className={inputCls}
            value={data.credit_score_range} onChange={(e) => set("credit_score_range", e.target.value)}>
            {SCORES.map((s) => <option key={s.v} value={s.v}>{s.l}</option>)}
          </select>
          {err && <p className="text-sm text-[#8a2412] mt-2 font-bold" data-testid="lead-error">{err}</p>}
          <div className="flex gap-2 mt-4">
            <Button variant="ghost" data-testid="lead-back-1" onClick={() => setStep(0)}
              className="rounded-full font-bold">Back</Button>
            <Button data-testid="lead-next-1" onClick={next}
              className="flex-1 bg-[#191c1f] hover:bg-black text-white rounded-full font-bold min-h-[44px]">
              Continue
            </Button>
          </div>
        </div>
      )}

      {step === 2 && (
        <div>
          <label className="flex items-start gap-3 text-sm leading-relaxed cursor-pointer">
            <input type="checkbox" data-testid="lead-consent" className="mt-1 w-5 h-5 accent-[#f06400]"
              checked={consent} onChange={(e) => setConsent(e.target.checked)} />
            <span>{settings.consent_text}</span>
          </label>
          <input type="text" name="hp" tabIndex={-1} autoComplete="off"
            style={{ position: "absolute", left: "-9999px" }} aria-hidden
            onChange={(e) => set("hp", e.target.value)} />
          {err && <p className="text-sm text-[#8a2412] mt-2 font-bold" data-testid="lead-error">{err}</p>}
          <div className="flex gap-2 mt-4">
            <Button variant="ghost" data-testid="lead-back-2" onClick={() => setStep(1)}
              className="rounded-full font-bold">Back</Button>
            <Button data-testid="lead-submit" disabled={busy} onClick={submit}
              className="flex-1 bg-[#f06400] hover:bg-[#d85800] text-[#191c1f] rounded-full font-extrabold min-h-[44px]">
              {busy ? "Sending…" : "Check my options"}
            </Button>
          </div>
          <p className="mt-3 text-xs text-[#6b6f76] text-center">
            Free to apply. We are a facilitator, not a lender.
          </p>
        </div>
      )}
    </div>
  );
}
